import { CampaignInterface, CampaignsResponseInterface } from '@app/models/campaigns';
import { getCampaigns } from '@app/services/campaigns';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { resetForm } from '@app/redux/features/campaignForm';

export const useCampaigns = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const useCreateCampaignModal = false;
  const [createSecheduleModal, setCreateScheduleModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState<CampaignInterface | undefined>();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const filterForm = useForm();
  const [searchValue, setSearchValue] = useState('');

  const {
    data: campaignsData,
    isLoading,
    refetch,
  } = useQuery<CampaignsResponseInterface>({
    queryKey: ['campaigns', page, limit, sortBy, sortOrder, filterForm.watch(), filterForm.watch('statuses'), filterForm.watch('scheduled'), filterForm.watch('platforms')],
    queryFn: () => {
      const formValues = filterForm.getValues();
      
      // Process filter values to match API expectations
      let statuses = undefined;
      let scheduled = undefined;
      
      if (formValues.statuses) {
        if (Array.isArray(formValues.statuses) && formValues.statuses.length > 0) {
          // Handle multi-select status filter
          statuses = formValues.statuses.map((status: string) => status.toUpperCase());
        } else if (!Array.isArray(formValues.statuses) && formValues.statuses !== 'all') {
          // Handle single status filter
          if (formValues.statuses === 'scheduled') {
            scheduled = true;
          } else {
            statuses = [formValues.statuses.toUpperCase()];
          }
        }
      }
      
      // Check for scheduled parameter from tab click
      if (formValues.scheduled) {
        scheduled = true;
        statuses = undefined; // Clear statuses when scheduled is true
      }
      
      const channels = formValues.channels && formValues.channels !== 'all' ? 
        (Array.isArray(formValues.channels) ? formValues.channels : [formValues.channels]) : undefined;
      // Always return all campaigns regardless of platform selection
      const platforms = undefined;
      
      return getCampaigns({
        statuses,
        channels,
        platforms,
        scheduled,
        from: formValues.from,
        to: formValues.to,
        name: searchValue,
        page,
        limit,
        sortBy: sortBy || 'createdAt',
        sortOrder,
      })
        .then((res) => {
          const responseData = res?.data?.data || res?.data;
          return responseData;
        })
        .catch((err) => {
          console.error('Error fetching campaigns:', err);
          // Return a valid data structure instead of the error
          return { campaigns: [], total: 0 };
        });
    },
  });

  const handleDeleteModal = (campaign?: CampaignInterface) => {
    setDeleteModal(campaign);
  };

  const handleCreateScheduleModal = () => {
    if (useCreateCampaignModal) {
      setCreateScheduleModal(!createSecheduleModal);
    } else {
      createCampaign();
    }
  };

  const createCampaign = () => {
    dispatch(resetForm());
    localStorage.removeItem('campaignFormData');
    localStorage.removeItem('campaignSelectedLists');
    navigate('/campaigns/create/settings');
  };

  const createAutomation = () => {
    localStorage.removeItem('automationFormData');
    localStorage.removeItem('automationSelectedLists');
    navigate('/automations/create/settings');
  };

  const handleLimitChange = (newLimit: number | undefined) => {
    setLimit(newLimit ?? 10);
    setPage(1);
  };

  const handleNextPage = () => {
    setPage((prev) => prev + 1);
  };

  const handlePreviousPage = () => {
    setPage((prev) => Math.max(prev - 1, 1));
  };

  const handleSortingTable = (field: string) => {
    // For platforms column, sort by name instead
    const actualSortField = field === 'platforms' ? 'name' : field;
    
    if (sortBy === actualSortField) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(actualSortField);
      setSortOrder('asc');
    }
  };

  useEffect(() => {
    // Refetch when form values change (excluding search field)
    const subscription = filterForm.watch((_, { name }) => {
      // Skip search field changes - they are handled separately
      if (name === 'search') {
        return;
      }
      refetch();
    });
    return () => subscription.unsubscribe();
  }, [filterForm, refetch]);

  // Handle search value changes separately
  useEffect(() => {
    // Only refetch if search value has 3+ characters or is empty
    if (searchValue.length >= 3 || searchValue === '') {
      refetch();
    }
  }, [searchValue, refetch]);
  return {
    handleCreateScheduleModal,
    createSecheduleModal,
    campaignsData,
    isLoading,
    refetch,
    createAutomation,
    createCampaign,
    handleDeleteModal,
    deleteModal,
    // Return pagination values and handlers
    page,
    limit,
    sortBy,
    sortOrder,
    handleLimitChange,
    handleNextPage,
    handlePreviousPage,
    handleSortingTable,
    filterForm,
    searchValue,
    setSearchValue,
  };
};
